/**
 * Performance Dashboard - Live Trading Analytics
 * Tracks real bot performance vs backtest predictions
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { botAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
  TrendingUp, TrendingDown, BarChart3, DollarSign, Target,
  Activity, Zap, AlertTriangle, Award, Clock, Shield,
  ArrowUpRight, ArrowDownRight, RefreshCw, Percent, ChevronDown, ChevronUp
} from 'lucide-react';

// ===================== CHART COMPONENTS =====================

const PnLCurve = ({ data }) => {
  if (!data || data.length < 2) return null;
  const width = 900, height = 280;
  const pad = { top: 20, right: 70, bottom: 35, left: 10 };
  const cw = width - pad.left - pad.right;
  const ch = height - pad.top - pad.bottom;

  const vals = data.map(d => d.pnl);
  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals);
  const range = (max - min) || 1;

  const toX = (i) => pad.left + (i / (data.length - 1)) * cw;
  const toY = (v) => pad.top + ch - ((v - min) / range) * ch;

  const points = data.map((d, i) => `${toX(i)},${toY(d.pnl)}`).join(' ');
  const zeroY = toY(0);
  const areaPoints = points + ` ${toX(data.length - 1)},${zeroY} ${toX(0)},${zeroY}`;
  const finalPnl = vals[vals.length - 1];
  const color = finalPnl >= 0 ? '#10b981' : '#ef4444';

  const yTicks = [min, min + range * 0.25, min + range * 0.5, min + range * 0.75, max];

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ minWidth: 600 }}>
        <defs>
          <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {yTicks.map((v, i) => {
          const y = toY(v);
          return (<g key={i}><line x1={pad.left} y1={y} x2={pad.left + cw} y2={y} stroke="#1e1e2e" strokeWidth="1" /><text x={pad.left + cw + 5} y={y + 4} fill="#6b7280" fontSize="10" fontFamily="monospace">${v >= 0 ? '+' : ''}{v.toFixed(0)}</text></g>);
        })}
        <line x1={pad.left} y1={zeroY} x2={pad.left + cw} y2={zeroY} stroke="#6b7280" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
        <polygon points={areaPoints} fill="url(#pnlGrad)" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Trade markers */}
        {data.map((d, i) => {
          if (data.length > 30 && i % 3 !== 0) return null;
          return (
            <circle key={i} cx={toX(i)} cy={toY(d.pnl)} r="3"
              fill={d.tradePnl >= 0 ? '#10b981' : '#ef4444'} stroke="#0d0d12" strokeWidth="1" opacity="0.7" />
          );
        })}
        <text x={pad.left} y={height - 5} fill="#6b7280" fontSize="10">{data[0].date}</text>
        <text x={pad.left + cw} y={height - 5} fill="#6b7280" fontSize="10" textAnchor="end">{data[data.length - 1].date}</text>
        <text x={pad.left + cw + 5} y={toY(finalPnl) - 8} fill={color} fontSize="11" fontFamily="monospace" fontWeight="bold">
          ${finalPnl >= 0 ? '+' : ''}{finalPnl.toFixed(0)}
        </text>
      </svg>
    </div>
  );
};

const WinRateChart = ({ data }) => {
  if (!data || data.length < 2) return null;
  const width = 900, height = 150;
  const pad = { top: 15, right: 70, bottom: 25, left: 10 };
  const cw = width - pad.left - pad.right;
  const ch = height - pad.top - pad.bottom;

  const toX = (i) => pad.left + (i / (data.length - 1)) * cw;
  const toY = (v) => pad.top + ch - (v / 100) * ch;

  const points = data.map((d, i) => `${toX(i)},${toY(d.winRate)}`).join(' ');
  const current = data[data.length - 1].winRate;
  const color = current >= 60 ? '#10b981' : current >= 45 ? '#eab308' : '#ef4444';

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ minWidth: 600 }}>
        <line x1={pad.left} y1={toY(50)} x2={pad.left + cw} y2={toY(50)} stroke="#eab308" strokeWidth="1" strokeDasharray="4,4" opacity="0.3" />
        <text x={pad.left + cw + 5} y={toY(50) + 4} fill="#eab308" fontSize="9" opacity="0.5">50%</text>
        <line x1={pad.left} y1={toY(0)} x2={pad.left + cw} y2={toY(0)} stroke="#1e1e2e" strokeWidth="1" />
        <line x1={pad.left} y1={toY(100)} x2={pad.left + cw} y2={toY(100)} stroke="#1e1e2e" strokeWidth="1" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <text x={pad.left + cw + 5} y={toY(current) + 4} fill={color} fontSize="11" fontFamily="monospace" fontWeight="bold">{current}%</text>
        <text x={pad.left} y={height - 3} fill="#6b7280" fontSize="9">Trade #1</text>
        <text x={pad.left + cw} y={height - 3} fill="#6b7280" fontSize="9" textAnchor="end">#{data.length}</text>
      </svg>
    </div>
  );
};

const BarChartHorizontal = ({ data, valueKey, labelKey, colorFn }) => {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => Math.abs(d[valueKey])), 1);

  return (
    <div className="space-y-2">
      {data.map((d, i) => {
        const val = d[valueKey];
        const pct = (Math.abs(val) / maxVal) * 100;
        const isPos = val >= 0;
        return (
          <div key={i} className="flex items-center gap-3">
            <span className="font-mono text-xs w-14 text-right">{d[labelKey]}</span>
            <div className="flex-1 h-6 bg-bg-tertiary rounded-lg overflow-hidden relative">
              <div className={`h-full rounded-lg ${isPos ? 'bg-accent-green/30' : 'bg-accent-red/30'}`} style={{ width: `${Math.max(pct, 2)}%` }} />
              <span className={`absolute right-2 top-1 text-xs font-mono ${colorFn ? colorFn(d) : (isPos ? 'text-accent-green' : 'text-accent-red')}`}>
                {isPos ? '+' : ''}{typeof val === 'number' ? `$${val.toFixed(0)}` : val}
              </span>
            </div>
            <span className="text-text-muted text-xs w-16 text-right">{d.trades}T • {d.winRate}%</span>
          </div>
        );
      })}
    </div>
  );
};

// ===================== MAIN COMPONENT =====================

const Performance = () => {
  const { hasAlpacaKeys } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backtestData, setBacktestData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[Performance] Fetching data...');
      const res = await botAPI.getPerformance();
      console.log('[Performance] Got response:', res.data?.empty, res.data?.overview?.totalTrades);
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      console.error('[Performance] Error:', msg, err.response?.status);
      setError(msg);
      toast.error('Failed to load: ' + msg);
    } finally {
      setLoading(false);
    }
  };

  // Try to load last optimizer results from localStorage-equivalent
  useEffect(() => {
    fetchData();
    // Backtest reference values (from your optimizer run)
    setBacktestData({ avgReturn: 2.69, winRate: 62.9, sharpe: 0.35, maxDD: 10.5, profitFactor: 6.5 });
  }, []);

  if (!hasAlpacaKeys) {
    return (<div className="min-h-screen p-6"><div className="card p-12 text-center"><Activity className="w-16 h-16 text-accent-blue mx-auto mb-4" /><h2 className="text-2xl font-bold mb-2">Performance Dashboard</h2><p className="text-text-secondary">Connect your Alpaca account.</p></div></div>);
  }

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          <div className="card p-12 text-center"><RefreshCw className="w-8 h-8 text-accent-blue animate-spin mx-auto mb-4" /><p className="text-text-secondary">Loading performance data...</p></div>
        </div>
      </div>
    );
  }

  if (!data || data.empty) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
          {error ? (
            <div className="card p-12 text-center">
              <AlertTriangle className="w-16 h-16 text-accent-red mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Error Loading Performance</h2>
              <p className="text-text-secondary mb-4">{error}</p>
              <button onClick={fetchData} className="bg-accent-blue text-white px-4 py-2 rounded-xl">Retry</button>
            </div>
          ) : (
            <div className="card p-12 text-center">
              <Activity className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">No Trades Yet</h2>
              <p className="text-text-secondary">Performance data will appear after the bot completes trades.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const o = data.overview;

  return (
    <div className="min-h-screen">
      <Navigation />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3"><Activity className="w-6 h-6 text-accent-blue" /> Performance Dashboard</h1>
            <p className="text-text-muted text-sm mt-1">{o.firstTradeDate} → {o.lastTradeDate} • {o.totalTrades} closed trades</p>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 bg-bg-tertiary hover:bg-bg-tertiary/80 text-text-secondary px-4 py-2 rounded-xl text-sm transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Overview cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: 'Total P&L', value: `${o.totalPnl >= 0 ? '+' : ''}$${o.totalPnl.toLocaleString()}`, color: o.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red', icon: DollarSign },
            { label: 'Win Rate', value: `${o.winRate}%`, color: o.winRate >= 50 ? 'text-accent-green' : 'text-accent-red', sub: `${o.wins}W / ${o.losses}L`, icon: Target },
            { label: 'Profit Factor', value: o.profitFactor >= 999 ? '∞' : o.profitFactor, color: o.profitFactor >= 1.5 ? 'text-accent-green' : o.profitFactor >= 1 ? 'text-accent-yellow' : 'text-accent-red', icon: BarChart3 },
            { label: 'Avg Win', value: `+$${o.avgWin}`, color: 'text-accent-green', icon: ArrowUpRight },
            { label: 'Avg Loss', value: `-$${o.avgLoss}`, color: 'text-accent-red', icon: ArrowDownRight },
            { label: 'Max Drawdown', value: `-$${o.maxDrawdown}`, color: o.maxDrawdown < 500 ? 'text-accent-green' : 'text-accent-red', icon: TrendingDown },
          ].map((c, i) => (
            <div key={i} className="card p-3">
              <div className="flex items-center gap-1.5 mb-1"><c.icon className="w-3.5 h-3.5 text-text-muted" /><span className="text-text-muted text-xs">{c.label}</span></div>
              <div className={`font-mono text-lg font-bold ${c.color}`}>{c.value}</div>
              {c.sub && <div className="text-text-muted text-xs">{c.sub}</div>}
            </div>
          ))}
        </div>

        {/* Live vs Backtest Comparison */}
        {backtestData && (
          <div className="card p-5 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-accent-blue" /> Live vs Backtest (Optimizer Prediction)</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { label: 'Win Rate', live: `${o.winRate}%`, backtest: `${backtestData.winRate}%`, diff: o.winRate - backtestData.winRate },
                { label: 'Profit Factor', live: o.profitFactor >= 999 ? '∞' : o.profitFactor.toString(), backtest: backtestData.profitFactor.toString(), diff: Math.min(o.profitFactor, 10) - backtestData.profitFactor },
                { label: 'Max Drawdown', live: `$${o.maxDrawdown}`, backtest: `${backtestData.maxDD}%`, diff: null },
                { label: 'ATR Stop', live: `${o.currentATRMultiplier}×`, backtest: '2.5×', diff: null },
                { label: 'Streaks', live: `${o.maxConsecWins}W / ${o.maxConsecLosses}L`, backtest: '—', diff: null },
              ].map((item, i) => (
                <div key={i} className="bg-bg-tertiary/50 rounded-xl p-3">
                  <div className="text-text-muted text-xs mb-2">{item.label}</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-text-muted uppercase">Live</div>
                      <div className="font-mono font-bold text-sm">{item.live}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-text-muted uppercase">Backtest</div>
                      <div className="font-mono text-sm text-text-secondary">{item.backtest}</div>
                    </div>
                  </div>
                  {item.diff !== null && (
                    <div className={`text-xs font-mono mt-1 ${item.diff >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                      {item.diff >= 0 ? '▲' : '▼'} {Math.abs(item.diff).toFixed(1)} {item.label === 'Win Rate' ? 'pts' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cumulative P&L */}
        <div className="card p-5 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-accent-green" /> Cumulative P&L</h3>
          <PnLCurve data={data.pnlCurve} />
        </div>

        {/* Rolling Win Rate */}
        <div className="card p-5 mb-6">
          <h3 className="font-semibold mb-1 flex items-center gap-2"><Percent className="w-4 h-4 text-accent-blue" /> Rolling Win Rate (10-trade window)</h3>
          <p className="text-text-muted text-xs mb-3">Tracks consistency — staying above 50% means the strategy is working</p>
          <WinRateChart data={data.rollingWinRate} />
        </div>

        {/* Two column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Per-Symbol Performance */}
          <div className="card p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-accent-yellow" /> Symbol Performance</h3>
            {data.symbolStats && data.symbolStats.length > 0 ? (
              <BarChartHorizontal
                data={data.symbolStats.slice(0, 15)}
                valueKey="totalPnl"
                labelKey="symbol"
                colorFn={(d) => d.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}
              />
            ) : (
              <p className="text-text-muted text-sm">No symbol data</p>
            )}
          </div>

          {/* Exit Reasons */}
          <div className="card p-5">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-accent-purple" /> Exit Reasons</h3>
            {data.exitReasons && Object.keys(data.exitReasons).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(data.exitReasons).sort((a, b) => b[1].count - a[1].count).map(([reason, d]) => (
                  <div key={reason} className="flex items-center justify-between bg-bg-tertiary/50 rounded-xl p-3">
                    <div>
                      <div className="font-medium text-sm capitalize">{reason.replace(/_/g, ' ')}</div>
                      <div className="text-text-muted text-xs">{d.count} exits • {d.count > 0 ? ((d.wins / d.count) * 100).toFixed(0) : 0}% profitable</div>
                    </div>
                    <div className={`font-mono font-bold text-sm ${d.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                      {d.totalPnl >= 0 ? '+' : ''}${d.totalPnl.toFixed(0)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-muted text-sm">No exit data</p>
            )}
          </div>
        </div>

        {/* Strategy Breakdown */}
        {data.strategyBreakdown && Object.keys(data.strategyBreakdown).length > 0 && (
          <div className="card p-5 mb-6">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-accent-orange" /> Strategy Breakdown</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(data.strategyBreakdown).map(([strat, d]) => {
                const winRate = d.trades > 0 ? ((d.wins / d.trades) * 100).toFixed(1) : 0;
                return (
                  <div key={strat} className="bg-bg-tertiary/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold capitalize">{strat}</span>
                      <span className={`font-mono font-bold ${d.totalPnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                        {d.totalPnl >= 0 ? '+' : ''}${d.totalPnl.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-text-muted">
                      <span>{d.trades} trades</span>
                      <span>{d.wins}W / {d.losses}L</span>
                      <span className={`font-mono ${winRate >= 50 ? 'text-accent-green' : 'text-accent-red'}`}>{winRate}%</span>
                    </div>
                    <div className="mt-2 h-2 bg-bg-primary rounded-full overflow-hidden">
                      <div className="h-full bg-accent-green rounded-full" style={{ width: `${winRate}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Daily P&L Table */}
        {data.dailyPnl && data.dailyPnl.length > 0 && (
          <DailyPnLTable data={data.dailyPnl} />
        )}

        {/* Extra stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1"><Zap className="w-3.5 h-3.5 text-accent-green" /><span className="text-text-muted text-xs">Largest Win</span></div>
            <div className="font-mono text-lg font-bold text-accent-green">+${o.largestWin}</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5 text-accent-red" /><span className="text-text-muted text-xs">Largest Loss</span></div>
            <div className="font-mono text-lg font-bold text-accent-red">${o.largestLoss}</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1"><ArrowUpRight className="w-3.5 h-3.5 text-accent-green" /><span className="text-text-muted text-xs">Best Streak</span></div>
            <div className="font-mono text-lg font-bold text-accent-green">{o.maxConsecWins} wins</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center gap-1.5 mb-1"><ArrowDownRight className="w-3.5 h-3.5 text-accent-red" /><span className="text-text-muted text-xs">Worst Streak</span></div>
            <div className="font-mono text-lg font-bold text-accent-red">{o.maxConsecLosses} losses</div>
          </div>
        </div>

      </div>
    </div>
  );
};

// ===== Daily P&L Table =====
const DailyPnLTable = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const displayed = expanded ? data : data.slice(-10);

  return (
    <div className="card overflow-hidden mb-6">
      <div className="p-4 border-b border-border-color flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-text-muted" /> Daily P&L</h3>
        {data.length > 10 && (
          <button onClick={() => setExpanded(!expanded)} className="text-accent-blue text-sm hover:underline flex items-center gap-1">
            {expanded ? <><ChevronUp className="w-3.5 h-3.5" /> Show recent</> : <><ChevronDown className="w-3.5 h-3.5" /> Show all {data.length} days</>}
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-text-muted text-xs border-b border-border-color">
            <th className="p-3 text-left">Date</th>
            <th className="p-3 text-right">P&L</th>
            <th className="p-3 text-right">Trades</th>
            <th className="p-3 text-right">Win Rate</th>
            <th className="p-3 text-left">Visual</th>
          </tr></thead>
          <tbody>
            {displayed.map((d, i) => {
              const wr = d.trades > 0 ? ((d.wins / d.trades) * 100).toFixed(0) : 0;
              const maxPnl = Math.max(...data.map(x => Math.abs(x.pnl)), 1);
              const barWidth = (Math.abs(d.pnl) / maxPnl) * 100;
              return (
                <tr key={i} className="border-b border-border-color/50 hover:bg-bg-tertiary/50">
                  <td className="p-3 font-mono text-xs">{d.date}</td>
                  <td className={`p-3 font-mono text-right font-bold ${d.pnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {d.pnl >= 0 ? '+' : ''}${d.pnl.toFixed(2)}
                  </td>
                  <td className="p-3 text-right text-text-muted">{d.trades}</td>
                  <td className={`p-3 font-mono text-right ${wr >= 50 ? 'text-accent-green' : 'text-accent-red'}`}>{wr}%</td>
                  <td className="p-3">
                    <div className="h-3 w-32 bg-bg-tertiary rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${d.pnl >= 0 ? 'bg-accent-green/50' : 'bg-accent-red/50'}`} style={{ width: `${Math.max(barWidth, 3)}%` }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Performance;
